// Step 5 — Quality selection. Three tiers with explicit credit cost shown.
// Free-tier users see Medium/High locked with an upgrade hint; non-free
// tiers can pick any. Cost preview is per-frame at the multiplier:
//   Low = 1 credit, Medium = 2 credits, High = 4 credits.
// Carousel posts multiply by 4 frames (shown in the summary at the bottom).

import { useEffect } from 'react';
import type { Quality, Provider } from '@/lib/api';
import type { PlanTier } from '@/hooks/useCredits';
import StepHeader from './StepHeader';

interface StepQualityProps {
  selected: Quality;
  onSelect: (q: Quality) => void;
  isCarousel: boolean;
  planTier: PlanTier;
  balance: number | null;
  // Experimental: image-gen provider toggle. Gemini is 1:1-only and ignores
  // the quality tier — frontend treats it as a flat 2-credit option.
  provider: Provider;
  onProviderChange: (p: Provider) => void;
  /** Current platform aspect ratio. We disable Gemini for anything other
   *  than 1:1 since the backend (and the Gemini model itself) only supports
   *  1:1 in this spike. */
  aspectRatio: string;
  /** When true, skip the StepHeader. /appv2's Section wrapper supplies
   *  its own header so the inner one would be a duplicate. */
  compact?: boolean;
}

interface Tier {
  id: Quality;
  label: string;
  multiplier: number;     // credits per frame
  description: string;
  icon: string;
  /** CSS filter applied to the sample image to *simulate* the quality
   *  difference visually. Real generation uses gpt-image-1's quality
   *  parameter; this is just an illustrative preview. */
  previewFilter: string;
}

// Single reference image used across all three tier cards. Bundled with
// the frontend (Frontend/public/templates/template3/frame1_thumb.png).
const PREVIEW_SRC = '/templates/template3/frame1_thumb.png';

// Tier IDs ('low' / 'medium' / 'high') stay frozen — the backend Quality
// type + Stripe pricing + credit RPCs all key off these. We only rename the
// user-facing LABELS so the UI reads less like "Low quality is bad" and
// more like "Regular is the default workhorse, Studio is the hero shot."
const TIERS: Tier[] = [
  {
    id: 'low',
    label: 'Regular',
    multiplier: 1,
    description: 'Solid output for most use cases. Great for social feeds and quick iteration.',
    icon: 'ri-flashlight-line',
    previewFilter: 'blur(2.5px) saturate(70%) contrast(85%)',
  },
  {
    id: 'medium',
    label: 'Enhanced',
    multiplier: 2,
    description: 'Sharper detail and richer color. Worth the extra credits when the post matters.',
    icon: 'ri-star-line',
    previewFilter: 'blur(0.8px) saturate(95%)',
  },
  {
    id: 'high',
    label: 'Studio',
    multiplier: 4,
    description: 'Studio-grade detail. Reserve for hero shots, launches, and printed material.',
    icon: 'ri-vip-crown-line',
    previewFilter: 'none',
  },
];

export default function StepQuality({
  selected,
  onSelect,
  isCarousel,
  planTier,
  balance,
  provider,
  onProviderChange,
  aspectRatio,
  compact,
}: StepQualityProps) {
  const frameCount = isCarousel ? 4 : 1;
  // Jul 24 tiering — Free + Starter are Regular-only; Enhanced/Studio unlock on
  // Pro & Business. (Mirrors backend ALLOWED_QUALITIES_BY_PLAN in credits.py.)
  const regularOnly = planTier === 'free' || planTier === 'starter';
  // Gemini is 1:1-only in this spike. Anything else disables the toggle and
  // snaps back to OpenAI (handled in the click handler).
  const geminiSupported = aspectRatio === '1:1';
  const isGemini = provider === 'gemini';

  // Guard against landing on this step with Gemini selected for a non-1:1
  // aspect (e.g. user went back to Step 1, switched platform to Reels 9:16,
  // came forward again). Force-revert to OpenAI in that case so we never
  // submit an invalid combo.
  useEffect(() => {
    if (isGemini && !geminiSupported) {
      onProviderChange('openai');
    }
  }, [isGemini, geminiSupported, onProviderChange]);

  return (
    // /app: h-full flex column inside the fixed-viewport wizard panel.
    // /appv2 (compact): no height constraint — content flows in the
    // page's natural scroll.
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {!compact && (
        <StepHeader
          icon="ri-vip-crown-line"
          title="Choose image quality"
          subtitle={
            <>
              Higher quality means more detail — and more credits per frame.
              {isCarousel && ' Carousel posts use 4 frames, so costs are multiplied.'}
            </>
          }
        />
      )}

      {/* Provider toggle removed from the UI — Gemini stays a 1:1-only
          spike that's not yet production-ready, so the user always gets
          OpenAI gpt-image-1 here. The `provider` / `onProviderChange`
          props stay in the prop signature because page.tsx still threads
          provider state through to /api/generate; the useEffect above
          force-snaps the value to 'openai' anyway when aspect ≠ 1:1.
          When Gemini is ready to ship, re-introduce the toggle here. */}

      <div className={compact ? '' : 'flex-1 min-h-0 overflow-y-auto'}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {TIERS.map((tier) => {
            const cost = tier.multiplier * frameCount;
            const locked = regularOnly && tier.id !== 'low';
            const isActive = selected === tier.id;
            const insufficient = balance !== null && balance < cost && !locked;
            return (
              <button
                key={tier.id}
                onClick={() => !locked && onSelect(tier.id)}
                disabled={locked}
                className={`
                  relative text-left p-5 rounded-2xl border-2 transition-all
                  ${locked
                    ? 'border-surface-dark/30 bg-surface/40 cursor-not-allowed opacity-70'
                    : isActive
                      ? 'border-primary bg-primary/5 shadow-md shadow-primary/10 cursor-pointer'
                      : 'border-surface-dark/30 bg-white hover:border-primary/40 hover:bg-primary/[0.02] cursor-pointer'}
                `}
              >
                {/* Lock overlay for non-free tiers when user is on free plan */}
                {locked && (
                  <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wide flex items-center gap-1 z-10">
                    <i className="ri-lock-line text-xs" />
                    Upgrade
                  </div>
                )}
                {/* Selected check */}
                {isActive && !locked && (
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary flex items-center justify-center z-10">
                    <i className="ri-check-line text-white text-sm" />
                  </div>
                )}

                {/* Quality preview thumbnail — same source image with a
                    quality-suggestive CSS filter so the visual difference
                    between tiers is obvious at a glance. */}
                <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-surface mb-4 border border-surface-dark/20">
                  <img
                    src={PREVIEW_SRC}
                    alt={`${tier.label} quality preview`}
                    className="w-full h-full object-cover"
                    style={{ filter: tier.previewFilter }}
                    loading="lazy"
                  />
                  <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded-md bg-dark/70 text-white text-xs font-bold uppercase tracking-wide">
                    {tier.label}
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                    locked
                      ? 'bg-surface-dark/40 text-dark-muted'
                      : isActive
                        ? 'bg-primary text-white'
                        : 'bg-primary/10 text-primary'
                  }`}>
                    <i className={`${tier.icon} text-sm`} />
                  </div>
                  <h3 className="font-display font-bold text-dark text-base">
                    {tier.label}
                  </h3>
                </div>
                <p className="text-xs text-dark-muted leading-relaxed mb-3">
                  {tier.description}
                </p>

                {/* Cost */}
                <div className="flex items-baseline gap-1.5">
                  <span className={`font-display font-bold text-lg ${
                    insufficient ? 'text-red-500' : 'text-dark'
                  }`}>
                    {cost}
                  </span>
                  <span className="text-xs text-dark-muted">
                    {cost === 1 ? 'credit' : 'credits'}
                    {isCarousel && ` (${tier.multiplier} × 4 frames)`}
                  </span>
                </div>
                {insufficient && (
                  <p className="mt-2 text-xs text-red-600 font-medium flex items-center gap-1">
                    <i className="ri-error-warning-line text-xs" />
                    Not enough credits
                  </p>
                )}
              </button>
            );
          })}
        </div>

        {/* Regular-only nudge (Free + Starter) */}
        {regularOnly && (
          <div className="mt-6 max-w-2xl mx-auto p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-amber-600 text-lg flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-0.5">
                  {planTier === 'free' ? "You're on the free trial." : "You're on the Starter plan."}
                </p>
                <p className="text-amber-800">
                  Only Regular quality is available. Upgrade to Pro to unlock
                  Enhanced and Studio quality.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
